import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Investment } from '../../types/Investment';
import { ScenarioInvestment } from '../../types/ScenarioManagement';
import { InvestmentList } from './InvestmentList';
import GlobalInvestmentSelector from './GlobalInvestmentSelector';
import { useToast } from '../../hooks/useToast';
import { useTheme } from '../../contexts/ThemeContext';
import { StandardButton } from '../ui';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { logger } from '../../services/LoggingService';

type ViewMode = 'list' | 'global-select';

interface InvestmentTabProps {
  investments?: ScenarioInvestment[];
  selectedInvestmentIds?: string[];
  onChange?: (investments: ScenarioInvestment[]) => void;
  onSelectedInvestmentIdsChange?: (selectedIds: string[]) => void;
}

const InvestmentTabComponent: React.FC<InvestmentTabProps> = ({ 
  investments = [], 
  selectedInvestmentIds = [],
  onChange,
  onSelectedInvestmentIdsChange
}) => {
  logger.debug('InvestmentTab rendering', 'InvestmentTab', {
    investmentCount: investments.length,
    selectedIdCount: selectedInvestmentIds.length
  });
  const { showToast } = useToast();
  const { isDark } = useTheme();
  const [currentMode, setCurrentMode] = useState<ViewMode>('list');
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Removed unused localInvestments state
  const [displayInvestments, setDisplayInvestments] = useState<ScenarioInvestment[]>([]);
  // Removed showImportDialog and availableInvestments - using global selection instead

  // Load investments based on selectedInvestmentIds
  useEffect(() => {
    const loadSelectedInvestments = async () => {
      if (selectedInvestmentIds.length === 0) {
        setDisplayInvestments([]);
        return;
      }

      try {
        const persistence = JSONPersistenceService.getInstance();
        const data = await persistence.loadData();
        
        if (data?.globalInvestments) {
          const selectedInvestments: ScenarioInvestment[] = [];
          
          selectedInvestmentIds.forEach(id => {
            const globalInvestment = data.globalInvestments?.find(([invId]) => invId === id)?.[1];
            if (globalInvestment) {
              // Convert GlobalInvestment to ScenarioInvestment
              selectedInvestments.push({
                id: globalInvestment.id,
                name: globalInvestment.name,
                icon: globalInvestment.icon,
                description: globalInvestment.description,
                cost: globalInvestment.cost,
                impact: globalInvestment.impact,
                businessOutcomes: globalInvestment.businessOutcomes,
                category: globalInvestment.category,
                enabled: true
              });
            }
          });
          
          setDisplayInvestments(selectedInvestments);
        }
      } catch (error) {
        logger.error('Failed to load selected investments', 'InvestmentTab', {
          selectedInvestmentIds
        }, error as Error);
      }
    };

    loadSelectedInvestments();
  }, [selectedInvestmentIds]);

  // Removed unused localInvestments update effect
  
  // Debug effect to monitor state changes
  useEffect(() => {
    logger.debug('InvestmentTab state changed', 'InvestmentTab', {
      currentMode,
      selectedInvestmentName: selectedInvestment?.name
    });
  }, [currentMode, selectedInvestment]);

  // Removed loadAvailableInvestments - using global selection instead

  const handleEdit = (investment: Investment) => {
    // Edit functionality disabled for scenario investments
    // Investments should be edited in the global investment library
    showToast({
      title: 'Edit in Investment Library',
      message: 'To edit investments, please use the Investment Library in the Management tab',
      type: 'info'
    });
  };




  const handleModeChange = (mode: ViewMode) => {
    setCurrentMode(mode);
    setSelectedInvestment(null);
  };
  

  return (
    <div className="space-y-4">
      {/* Simplified Header for Tab Context */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Investment Configuration
          </h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'} mt-1`}>
            Select which investments from the library are available in this scenario
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <StandardButton
            type="button"
            onClick={() => handleModeChange('global-select')}
            variant={currentMode === 'global-select' ? 'primary' : 'secondary'}
            size="small"
            leftIcon="🌐"
          >
            Select from Investment Library
          </StandardButton>
          
        </div>
      </div>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentMode}-${selectedInvestment?.id || 'new'}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {currentMode === 'list' && (
            <InvestmentList
                investments={displayInvestments}
                onEdit={handleEdit}
              onToggle={(investmentId, enabled) => {
                const updatedInvestments = displayInvestments.map(inv => 
                  inv.id === investmentId ? { ...inv, enabled } : inv
                );
                setDisplayInvestments(updatedInvestments);
                // Note: We don't update the investments array, only the display state
                // The actual investment configuration is managed through selectedInvestmentIds
              }}
              onDelete={(investmentId) => {
                // Remove from selectedInvestmentIds instead of deleting the investment
                const updatedIds = selectedInvestmentIds.filter(id => id !== investmentId);
                onSelectedInvestmentIdsChange?.(updatedIds);
                showToast({ 
                  title: 'Investment Removed', 
                  message: 'Investment removed from this scenario',
                  type: 'success' 
                });
              }}
              refreshTrigger={refreshTrigger}
            />
          )}

          {currentMode === 'global-select' && (
            <div className="space-y-4">
              {/* Header */}
              <div className={`p-4 rounded-lg border ${
                isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
              }`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Investment Library Selection
                </h4>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  Select from the investment library to make available in this scenario. 
                  These investments will be available during scenario gameplay.
                </p>
              </div>

              <GlobalInvestmentSelector
                selectedInvestmentIds={selectedInvestmentIds}
                onChange={(newSelectedIds) => {
                  onSelectedInvestmentIdsChange?.(newSelectedIds);
                  showToast({
                    title: 'Investment Selection Updated',
                    message: `${newSelectedIds.length} investments selected for this scenario`,
                    type: 'success'
                  });
                }}
                maxSelections={10}
              />

              <div className="flex justify-end gap-3">
                <StandardButton
                  type="button"
                  onClick={() => setCurrentMode('list')}
                  variant="secondary"
                >
                  Cancel
                </StandardButton>
                <StandardButton
                  type="button"
                  onClick={() => {
                    setCurrentMode('list');
                    // Trigger refresh to show updated investments
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  variant="primary"
                >
                  Done
                </StandardButton>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

    </div>
  );
};

export const InvestmentTab = React.memo(InvestmentTabComponent);